import { createRouter, createWebHistory } from 'vue-router'
import { useAppUpdate, reloadForUpdate, startUpdateWatcher } from './composables/useAppUpdate'
import { useAuth } from './composables/useAuth'
import { useCurrency } from './composables/useCurrency'
import { trackPageView } from './utils/activityTracker'

// Import des composants de page (Lazy Loading)
const DashboardPage = () => import('./pages/DashboardPage.vue')
const ProjectsPage = () => import('./pages/ProjectsPage.vue')
const ProjectDetailPage = () => import('./pages/ProjectDetailPage.vue')
const ResourcesPage = () => import('./pages/ResourcesPage.vue')
const PlanningPage = () => import('./pages/PlanningPage.vue')
const GlobalPlanningPage = () => import('./pages/GlobalPlanningPage.vue')
const BudgetPage = () => import('./pages/BudgetPage.vue')
const PersonnelPage = () => import('./pages/PersonnelPage.vue')
const MaterielPage = () => import('./pages/MaterielPage.vue')
const DailyExpenseReportPage = () => import('./pages/DailyExpenseReportPage.vue')
const ProfilePage = () => import('./pages/ProfilePage.vue')
const SettingsPage = () => import('./pages/SettingsPage.vue')
const LoginPage = () => import('./pages/LoginPage.vue')
const ResetPasswordPage = () => import('./pages/ResetPasswordPage.vue')
const NotificationDemo = () => import('./pages/NotificationDemo.vue')
const DialogDemo = () => import('./pages/DialogDemo.vue')
const AdminDashboard = () => import('./pages/AdminDashboard.vue')
const AdminDashboardHome = () => import('./components/admin/AdminDashboardHome.vue')
const UserManagement = () => import('./components/admin/UserManagement.vue')
const UserProfile = () => import('./components/admin/user-profile/UserProfile.vue')
const PermissionManager = () => import('./components/admin/PermissionManager.vue')
const AISettings = () => import('./components/admin/AISettings.vue')
const SystemSettings = () => import('./components/admin/SystemSettings.vue')
const AuditLogs = () => import('./components/admin/AuditLogs.vue')

const TeamManagement = () => import('./components/director/TeamManagement.vue')

const FeedbackPage = () => import('./pages/FeedbackPage.vue')
const FeedbackManagement = () => import('./components/admin/FeedbackManagement.vue')

// Site Reports (Pointeurs & Consultation)
const SiteReportsPage = () => import('./pages/SiteReportsPage.vue')
const SiteReportsOverviewPage = () => import('./pages/SiteReportsOverviewPage.vue')
const SiteReportValidationPage = () => import('./pages/SiteReportValidationPage.vue')

const routes = [
  { path: '/', component: DashboardPage, name: 'dashboard', meta: { requiresAuth: true } },
  { path: '/projects', component: ProjectsPage, name: 'projects', meta: { requiresAuth: true } },
  { path: '/projects/:id', component: ProjectDetailPage, name: 'project-detail', props: true, meta: { requiresAuth: true } },
  { path: '/team-management', component: TeamManagement, name: 'team-management', meta: { requiresAuth: true, permission: 'users.create' } }, // Using permission check
  { path: '/profile', component: ProfilePage, name: 'profile', meta: { requiresAuth: true } },
  { path: '/settings', component: SettingsPage, name: 'settings', meta: { requiresAuth: true } },
  { path: '/resources', component: ResourcesPage, name: 'resources', meta: { requiresAuth: true, permission: 'resources.view' } },
  { path: '/planning', component: PlanningPage, name: 'planning', meta: { requiresAuth: true, roles: ['admin', 'superadmin', 'directeur', 'cost_controller', 'user'] } },
  { path: '/planning/global', component: GlobalPlanningPage, name: 'planning-global', meta: { requiresAuth: true, roles: ['admin', 'superadmin'] } },
  { path: '/budget', component: BudgetPage, name: 'budget', meta: { requiresAuth: true, roles: ['admin', 'superadmin'] } },
  { path: '/personnel', component: PersonnelPage, name: 'personnel', meta: { requiresAuth: true, permission: 'personnel.view' } },
  { path: '/personnel/report', component: () => import('./pages/PersonnelReportPage.vue'), name: 'personnel-report', meta: { requiresAuth: true, permission: 'personnel.view' } },
  { path: '/personnel/reconciliation', component: () => import('./pages/PosteReconciliationPage.vue'), name: 'poste-reconciliation', meta: { requiresAuth: true, permission: 'personnel.view' } },
  { path: '/activity/report', component: () => import('./pages/ActivityReportPage.vue'), name: 'activity-report', meta: { requiresAuth: true } },
  { path: '/feedback', component: FeedbackPage, name: 'feedback', meta: { requiresAuth: true } },
  { path: '/materiel', component: MaterielPage, name: 'materiel', meta: { requiresAuth: true, roles: ['admin', 'superadmin', 'directeur', 'cost_controller', 'user'] } },
  { path: '/materiel/reconciliation', component: () => import('./pages/MaterielReconciliationPage.vue'), name: 'materiel-reconciliation', meta: { requiresAuth: true, roles: ['admin', 'superadmin', 'directeur', 'cost_controller', 'user'] } },
  { path: '/materiel/report', component: () => import('./pages/MaterielReportPage.vue'), name: 'materiel-report', meta: { requiresAuth: true, roles: ['admin', 'superadmin', 'directeur', 'cost_controller', 'user'] } },
  { path: '/budget-control', component: () => import('./pages/BudgetControlPage.vue'), name: 'budget-control', meta: { requiresAuth: true } },
  { path: '/activity-liquidity', component: () => import('./pages/ActivityLiquidityPage.vue'), name: 'activity-liquidity', meta: { requiresAuth: true, roles: ['admin', 'superadmin'] } },
  { path: '/daily-expense-reports', component: DailyExpenseReportPage, name: 'daily-expense-reports', meta: { requiresAuth: true } },
  { path: '/notifications-demo', component: NotificationDemo, name: 'notifications-demo', meta: { requiresAuth: true } },
  { path: '/notifications', component: () => import('./pages/NotificationsPage.vue'), name: 'notifications', meta: { requiresAuth: true } },
  // Aide intégrée : obtient un lien signé puis redirige vers /aide (servi par Laravel, pages selon les droits)
  { path: '/documentation', component: () => import('./pages/DocumentationRedirect.vue'), name: 'documentation', meta: { requiresAuth: true } },
  { path: '/reallocation/:projectId/:periodStart/:periodEnd/:activityId/:source', component: () => import('./pages/ReallocationPage.vue'), name: 'reallocation', props: true, meta: { requiresAuth: true } },

  // Site Reports
  { 
    path: '/site-reports/overview', 
    component: SiteReportsOverviewPage, 
    name: 'site-reports-overview', 
    meta: { 
      requiresAuth: true, 
      roles: ['admin', 'superadmin', 'directeur', 'cost_controller', 'conducteur_travaux'] 
    } 
  },
  { path: '/site-reports', component: SiteReportsPage, name: 'site-reports', meta: { requiresAuth: true, permission: 'site_reports.view' } },
  { path: '/site-reports/create', component: () => import('./pages/SiteReportFormPage.vue'), name: 'site-reports-create', meta: { requiresAuth: true, permission: 'site_reports.create' } },
  { path: '/site-reports/:id', component: () => import('./pages/SiteReportFormPage.vue'), name: 'site-reports-edit', meta: { requiresAuth: true, permission: 'site_reports.view' } },
  { path: '/site-reports/:id/view', component: () => import('./pages/SiteReportViewPage.vue'), name: 'site-report-view', meta: { requiresAuth: true, permission: 'site_reports.validate' } },
  { path: '/site-reports/validation', component: SiteReportValidationPage, name: 'site-reports-validation', meta: { requiresAuth: true, permission: 'site_reports.validate' } },

  // Photos Chantier (Pointeurs)
  { path: '/photos-chantier', component: () => import('./pages/PointeurPhotoPage.vue'), name: 'pointeur-photos', meta: { requiresAuth: true, permission: 'site_reports.create' } },
  { path: '/suivi-planning', component: () => import('./pages/PlanningTrackingPage.vue'), name: 'planning-tracking', meta: { requiresAuth: true, permission: 'planning_tracking.view' } },

  // QHSE
  { path: '/qhse', component: () => import('./pages/QhsePaysPage.vue'), name: 'qhse-pays', meta: { requiresAuth: true, roles: ['qhse_pays', 'qhse_chantier', 'relais_qhse', 'superadmin', 'admin'] } },
  {
    path: '/admin',
    component: AdminDashboard,
    name: 'admin',
    meta: { requiresAuth: true, requiresAdmin: true },
    redirect: '/admin/dashboard',
    children: [
      { path: 'dashboard', component: AdminDashboardHome, name: 'admin-dashboard' },
      { path: 'users', component: UserManagement, name: 'admin-users' },
      { path: 'users/:id(\\d+)', component: UserProfile, name: 'admin-user-profile' },
      { path: 'permissions', component: PermissionManager, name: 'admin-permissions' },
      { path: 'ai-settings', component: AISettings, name: 'admin-ai-settings' },
      { path: 'fuel-config', component: () => import('./pages/admin/FuelConfigPage.vue'), name: 'admin-fuel-config' },
      { path: 'budget-control-config', component: () => import('./pages/admin/BudgetControlConfigPage.vue'), name: 'admin-budget-control-config' },
      { path: 'branding', component: () => import('./pages/admin/BrandingSettingsPage.vue'), name: 'admin-branding' },
      { path: 'general-settings', component: () => import('./pages/admin/GeneralSettingsPage.vue'), name: 'admin-general-settings' },
      { path: 'system', component: SystemSettings, name: 'admin-system' },
      { path: 'audit-logs', component: AuditLogs, name: 'admin-audit-logs' },
      { path: 'feedback', component: FeedbackManagement, name: 'admin-feedback' },
      { path: 'email-settings', component: () => import('./components/admin/EmailSettings.vue'), name: 'admin-email-settings' },
      { path: 'notification-settings', component: () => import('./pages/admin/NotificationSettingsPage.vue'), name: 'admin-notification-settings' },
      { path: 'send-notifications', component: () => import('./pages/admin/SendNotificationPage.vue'), name: 'admin-send-notifications' }
    ]
  },

  // Password Reset
  { path: '/reset-password/:token', component: ResetPasswordPage, name: 'reset-password' },

  // Routes d'erreur
  { path: '/error/404', component: () => import('./pages/errors/NotFoundPage.vue'), name: 'error-404' },
  { path: '/error/403', component: () => import('./pages/errors/ForbiddenPage.vue'), name: 'error-403' },
  { path: '/error/500', component: () => import('./pages/errors/ServerErrorPage.vue'), name: 'error-500' },

  { path: '/login', component: LoginPage, name: 'login' },

  // Lien public du registre des plaintes et réclamations (sans connexion)
  { path: '/plaintes-reclamations', component: () => import('./pages/PublicComplaintPage.vue'), name: 'public-complaint' },
  { path: '/plaintes-reclamations/avis/:token', component: () => import('./pages/PublicComplaintFeedbackPage.vue'), name: 'public-complaint-feedback' },

  // Catch-all route for 404
  { path: '/:pathMatch(.*)*', redirect: '/error/404' }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_APP_BASE_URL || '/'),
  routes
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  // Nouvelle version déployée : on profite du changement de page pour recharger l'application
  if (useAppUpdate().state.updateAvailable && to.fullPath !== from.fullPath) {
    if (reloadForUpdate(to.fullPath)) return
  }
  const { isAuthenticated, fetchUser, user } = useAuth()

  if (to.meta.requiresAuth && !isAuthenticated.value) {
    // User is not authenticated, redirect to login
    next('/login')
  } else if (to.path === '/login' && isAuthenticated.value && !to.query.logout) {
    // User is authenticated but trying to access login, redirect to dashboard
    // (?logout=1: logout in progress, the session is closed right after this navigation)
    next('/')
  } else {
    // Try to fetch user data if authenticated and not already loaded
    if (isAuthenticated.value) {
      if (!user.value) {
        await fetchUser()
      }

      // Ensure currency symbol is loaded once authenticated
      const { loadCurrencySymbol } = useCurrency()
      loadCurrencySymbol()
    }

    // Check admin access for admin routes
    if (to.meta.requiresAdmin) {
      const hasAdminAccess = user.value?.role === 'superadmin' ||
        (user.value?.permissions && user.value.permissions.includes('admin.access'));

      if (!hasAdminAccess) {
        next('/')
        return
      }
    }

    // Check specific permission if defined
    if (to.meta.permission) {
      // We need to check if useAuth().hasPermission works here, or manually check
      // useAuth is reactive, but we have the user object here already
      const hasPermission = user.value?.role === 'superadmin' ||
        (user.value?.permissions && user.value.permissions.includes(to.meta.permission));

      if (!hasPermission) {
        next('/') // Redirect to dashboard or 403 page
        return
      }
    }

    // Check specific roles if defined
    if (to.meta.roles) {
      const hasRole = to.meta.roles.includes(user.value?.role);
      if (!hasRole) {
        next('/');
        return;
      }
    }

    next()
  }
})

// Suivi d'utilisation : page consultée + temps passé
// Un fichier (chunk) de l'ancienne version n'existe plus sur le serveur : on recharge
router.onError((error, to) => {
  if (/dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(error?.message || '')) {
    reloadForUpdate(to?.fullPath)
  }
})
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  reloadForUpdate()
})
startUpdateWatcher()

router.afterEach((to, from) => {
  trackPageView(to, from)
})

export default router
